import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

const useStyles = makeStyles(theme => ({
  background: {
    background: '#f5f6fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    boxShadow: '0 4px 32px #0001',
    background: '#fff',
    padding: '36px 32px 30px 32px',
    margin: '0 auto',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  icons: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    '& img': {
      height: 26,
      width: 'auto',
    },
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '19px',
    marginBottom: 14,
  },
  label: {
    fontWeight: 500,
    fontSize: 14,
    color: '#222',
    marginBottom: 3,
    marginTop: 12,
  },
  boldLabel: {
    fontWeight: 700,
    marginTop: 18,
    marginBottom: 8,
    fontSize: 15,
    color: '#222',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    width: '100%',
    padding: 0,
  },
  mpField: {
    height: 40,
    borderRadius: 4,
    border: '1px solid #e3e8ee',
    padding: '0 13px',
    fontSize: 16,
    background: '#fafbfc',
    width: '100%',
    margin: '0 0 2px 0',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    '& iframe': {
      height: 40,
      minHeight: 36,
      maxHeight: 44,
      border: 'none',
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
    },
  },
  flexRow: {
    display: 'flex',
    gap: 14,
    width: '100%',
    margin: 0,
  },
  input: {
    minHeight: 40,
    borderRadius: 4,
    border: '1px solid #e3e8ee',
    padding: '0 13px',
    fontSize: 16,
    background: '#fafbfc',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
    marginTop: 0,
    marginBottom: 0,
    '&:focus': {
      borderColor: '#009ee3',
    },
  },
  button: {
    marginTop: 20,
    background: '#2176ff',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontWeight: 500,
    fontSize: 18,
    cursor: 'pointer',
    boxShadow: '0 2px 8px ',
    textTransform: 'none',
    letterSpacing: 0.2,
    transition: 'background 0.2s',
    '&:hover': {
      background: '#185abc',
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  lock: {
    fontSize: 18,
    marginRight: 7,
  },
}));

const PUBLIC_KEY = process.env.REACT_APP_API_KEY_MERCADOPAGO;
export default function MercadoPagoForm({
  amount,
  email,
  save,
  purchasingPlan,
}) {
  const classes = useStyles();
  const formRef = useRef(null);
  const mountedRef = useRef(false);
  const cardFormRef = useRef(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const uid = useMemo(() => Math.random().toString(36).slice(2), []);
  const ids = useMemo(
    () => ({
      form: `form-checkout-${uid}`,
      cardNumber: `form-checkout__cardNumber-${uid}`,
      expirationDate: `form-checkout__expirationDate-${uid}`,
      securityCode: `form-checkout__securityCode-${uid}`,
      cardholderName: `form-checkout__cardholderName-${uid}`,
      issuer: `form-checkout__issuer-${uid}`,
      installments: `form-checkout__installments-${uid}`,
      identificationType: `form-checkout__identificationType-${uid}`,
      identificationNumber: `form-checkout__identificationNumber-${uid}`,
      cardholderEmail: `form-checkout__cardholderEmail-${uid}`,
      submit: `form-checkout__submit-${uid}`,
    }),
    [uid]
  );

  useEffect(() => {
    if (mountedRef.current) return;

    const ensureScript = () =>
      new Promise(resolve => {
        if (window.MercadoPago) return resolve();
        const s = document.createElement('script');
        s.src = 'https://sdk.mercadopago.com/js/v2';
        s.onload = resolve;
        document.body.appendChild(s);
      });

    const init = async () => {
      await ensureScript();
      const mp = new window.MercadoPago(PUBLIC_KEY, { locale: 'es-AR' });

      const cardForm = mp.cardForm({
        amount: amount?.toString(),
        iframe: true,
        form: {
          id: ids.form,
          cardNumber: { id: ids.cardNumber, placeholder: 'Número de tarjeta' },
          expirationDate: { id: ids.expirationDate, placeholder: 'MM/YY' },
          securityCode: {
            id: ids.securityCode,
            placeholder: 'Código de seguridad',
          },
          cardholderName: {
            id: ids.cardholderName,
            placeholder: 'Titular de la tarjeta',
          },
          issuer: { id: ids.issuer, placeholder: 'Banco emisor' },
          installments: { id: ids.installments, placeholder: 'Cuotas' },
          identificationType: {
            id: ids.identificationType,
            placeholder: 'Tipo de documento',
          },
          identificationNumber: {
            id: ids.identificationNumber,
            placeholder: 'Número del documento',
          },
          cardholderEmail: { id: ids.cardholderEmail, placeholder: 'E-mail' },
        },
        callbacks: {
          onFormMounted: error => {
            if (error) console.warn('Error montando el form:', error);
          },
          onSubmit: async event => {
            event.preventDefault();
            setSubmitting(true);
            try {
              setFieldErrors({});
              const { token } = cardForm.getCardFormData();
              if (!token) {
                throw new Error(
                  'No se pudo obtener el token de la tarjeta. Verifica los datos ingresados.'
                );
              }
              await save(token);
            } catch (err) {
              console.error(err);

              const arr =
                (Array.isArray(err) && err) ||
                err?.causes ||
                err?.cause ||
                err?.error?.causes ||
                err?.errors ||
                [];

              if (Array.isArray(arr) && arr.length) {
                const map = arr.reduce((acc, e) => {
                  const key = e.field || 'general';
                  const msg = e.message || 'Error de validación.';
                  acc[key] = acc[key] ? `${acc[key]} ${msg}` : msg;
                  return acc;
                }, {});
                setFieldErrors(map);
                setError('Revisá los campos marcados.');
              } else {
                setError(
                  err.message || 'Ocurrió un error al procesar el pago.'
                );
              }

              setOpen(true);
            } finally {
              setSubmitting(false);
            }
          },
          onError: err => {
            const arr =
              (Array.isArray(err) && err) ||
              err?.causes ||
              err?.error?.causes ||
              err?.errors ||
              [];

            if (
              Array.isArray(arr) &&
              arr.length &&
              typeof setFieldErrors === 'function'
            ) {
              const map = arr.reduce((acc, e) => {
                const key = e.field || 'general';
                const msg = e.message || 'Error de validación.';
                acc[key] = acc[key] ? `${acc[key]} ${msg}` : msg;
                return acc;
              }, {});
              setFieldErrors(map);
              setError('Completa los campos del formulario.');
            } else {
              setError('No se pudo enviar el formulario. Revisá los datos.');
            }
            setOpen(true);
          },
          onFetching: () => () => {},
        },
      });

      cardFormRef.current = cardForm;
      mountedRef.current = true;
    };

    const raf = requestAnimationFrame(init);

    return () => {
      cancelAnimationFrame(raf);
      try {
        cardFormRef.current?.unmount?.();
      } catch {}
      if (formRef.current) {
        formRef.current.innerHTML = '';
      }
      cardFormRef.current = null;
      mountedRef.current = false;
    };
  }, [amount, ids]);

  useEffect(() => {
    if (email) {
      const t = setTimeout(() => {
        const emailInput = document.getElementById(ids.cardholderEmail);
        if (emailInput && !emailInput.value) {
          emailInput.value = email;
        }
      }, 700);
      return () => clearTimeout(t);
    }
  }, [email, ids.cardholderEmail]);

  return (
    <Box className={classes.background}>
      <Box className={classes.card}>
        <div className={classes.topRow}>
          <Typography className={classes.sectionTitle}>
            Tarjeta de crédito o débito
          </Typography>
        </div>

        <form
          id={ids.form}
          ref={formRef}
          autoComplete="off"
          className={classes.form}
        >
          <label className={classes.label}>Número de tarjeta</label>
          <div id={ids.cardNumber} className={classes.mpField} />
          {fieldErrors.cardNumber && (
            <Typography color="error" variant="caption">
              {fieldErrors.cardNumber}
            </Typography>
          )}

          <div className={classes.flexRow}>
            <div style={{ flex: 1 }}>
              <label className={classes.label}>Vencimiento</label>
              <div id={ids.expirationDate} className={classes.mpField} />
              {fieldErrors.expirationDate && (
                <Typography color="error" variant="caption">
                  {fieldErrors.expirationDate}
                </Typography>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label className={classes.label}>Código de seguridad</label>
              <div id={ids.securityCode} className={classes.mpField} />
              {fieldErrors.securityCode && (
                <Typography color="error" variant="caption">
                  {fieldErrors.securityCode}
                </Typography>
              )}
            </div>
          </div>

          <label className={classes.label}>
            Nombre del titular como aparece en la tarjeta
          </label>
          <input
            id={ids.cardholderName}
            className={classes.input}
            placeholder="María López"
          />

          <label className={classes.label}>Banco emisor</label>
          <select id={ids.issuer} className={classes.mpField} />

          <label className={classes.label}>Cuotas</label>
          <select id={ids.installments} className={classes.mpField} />

          <label className={classes.label}>Documento del titular</label>
          <div className={classes.flexRow}>
            <select
              id={ids.identificationType}
              className={classes.input}
              style={{ flex: 1 }}
            />
            <input
              id={ids.identificationNumber}
              className={classes.input}
              placeholder="99999999"
              style={{ flex: 3, marginLeft: 8 }}
            />
          </div>

          <Typography className={classes.boldLabel}>
            Completá tu información
          </Typography>
          <label className={classes.label}>E-mail</label>
          <input
            id={ids.cardholderEmail}
            className={classes.input}
            placeholder="ejemplo@email.com"
          />
          {fieldErrors.cardholderEmail && (
            <Typography color="error" variant="caption">
              {fieldErrors.cardholderEmail}
            </Typography>
          )}

          <Button
            id={ids.submit}
            type="submit"
            variant="contained"
            fullWidth
            className={classes.button}
            disabled={purchasingPlan}
            disableElevation
          >
            {purchasingPlan ? (
              <>
                <CircularProgress size={20} />
                Procesando…
              </>
            ) : (
              <>
                <LockOutlinedIcon className={classes.lock} />
                Pagar
              </>
            )}
          </Button>
        </form>
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>{error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
